<template>
  <transition name="toast-fade">
    <div
        v-if="isVisible"
        class="announcement-toast"
        :class="`severity-${severity}`"
        :style="{ top: navbarOffset + 'px' }"
        @click="toggleExpanded"
    >
      <span class="announcement-icon" aria-hidden="true">
        <svg
          v-if="severity === 'info'"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="currentColor"
        >
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="currentColor"
        >
          <path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      </span>

      <div class="announcement-body">
        <p v-if="!isExpanded" class="announcement-text">
          {{ primaryMessage.text }}
        </p>
        <ul v-else class="announcement-list">
          <li v-for="(msg, i) in sortedMessages" :key="i">{{ msg.text }}</li>
        </ul>
      </div>

      <button
        class="announcement-close"
        aria-label="Dismiss announcements"
        @click.stop="dismiss"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z" />
        </svg>
      </button>
    </div>
  </transition>
</template>

<script lang="ts">
import { STRINGS } from '@/constants';
import { Options, Vue } from 'vue-class-component';

type Severity = 'info' | 'warning' | 'important';

interface Announcement {
  text: string;
  type?: Severity;
}

const SEVERITY_ORDER: Record<Severity, number> = {
  info: 0,
  warning: 1,
  important: 2,
};

@Options({})
export default class AnnouncementToast extends Vue {
  isExpanded: boolean = false;
  dismissed: boolean = false;
  loading: boolean = true;
  messages: Announcement[] = [];
  navbarOffset: number = 80;

  private navbarEl: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  async mounted(): Promise<void> {
    this.setupNavbarObserver();
    await this.fetchAnnouncements();
  }

  beforeUnmount(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.navbarEl = null;
  }

  private setupNavbarObserver(): void {
    this.navbarEl = document.querySelector('.navbar');
    if (!this.navbarEl) return;

    const measure = () => {
      if (this.navbarEl) {
        this.navbarOffset = this.navbarEl.offsetHeight + 16;
      }
    };

    measure();
    this.resizeObserver = new ResizeObserver(measure);
    this.resizeObserver.observe(this.navbarEl);
  }

  private async fetchAnnouncements(): Promise<void> {
    try {
      const url = `${STRINGS.ANNOUNCEMENTS_URL}?t=${Date.now()}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      const data = await res.json();

      // JSONsilo wraps the payload; fall back to a bare array if unwrapped.
      const raw = Array.isArray(data) ? data : data?.VUE_APP_ANNOUNCEMENTS;

      if (Array.isArray(raw)) {
        this.messages = raw.filter(
          (m: any) =>
            m && typeof m.text === 'string' && m.text.trim() !== ''
        );
      }
    } catch (err) {
      console.warn('[AnnouncementToast] Failed to load announcements:', err);
    } finally {
      this.loading = false;
    }
  }

  get sortedMessages(): Announcement[] {
    return [...this.messages].sort(
      (a, b) =>
        SEVERITY_ORDER[b.type || 'info'] - SEVERITY_ORDER[a.type || 'info']
    );
  }

  get primaryMessage(): Announcement {
    return this.sortedMessages[0] || { text: '', type: 'info' };
  }

  get severity(): Severity {
    return this.primaryMessage.type || 'info';
  }

  get isVisible(): boolean {
    return !this.loading && this.messages.length > 0 && !this.dismissed;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  dismiss(): void {
    this.dismissed = true;
  }
}
</script>

<style scoped>
.announcement-toast {
  position: fixed;
  top: 5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 480px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  color: #fff;
  font-size: 0.95rem;
  line-height: 1.35;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  z-index: 2;
}

.severity-info      { background-color: #2563eb; }
.severity-warning   { background-color: #d9822b; }
.severity-important { background-color: #dc2626; }

.announcement-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.announcement-body {
  flex: 1;
  min-width: 0;
}

.announcement-text {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.announcement-list {
  margin: 0;
  padding-left: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.announcement-list li {
  overflow-wrap: break-word;
  white-space: pre-line;
  text-align: left;
  line-height: 1.3;
}

.announcement-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  margin-top: 1px;
}

.announcement-close:hover {
  opacity: 1;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}
</style>